/**
 * MODULE: domain/sales-presentations/frontend/context/PresentationEditorStoreContext
 * PURPOSE: React context + useSyncExternalStore for presentationEditorStore (canvas shell).
 * DEPENDENCIES: react, ../store/presentationEditorStore
 * PUBLIC_API: PresentationEditorStoreProvider, usePresentationEditorStoreFromContext, usePresentationEditorState
 * SIDE_EFFECTS: none
 * AGENT_SAFETY: 🟢 SAFE
 * FILE_SIZE: ~55 / 200
 */

import {
  createContext,
  useContext,
  useSyncExternalStore,
  type ReactNode,
} from 'react';
import type {
  PresentationEditorStore,
  PresentationEditorState,
} from '../store/presentationEditorStore';

const PresentationEditorStoreContext = createContext<PresentationEditorStore | null>(null);

export function PresentationEditorStoreProvider({
  store,
  children,
}: {
  store: PresentationEditorStore;
  children: ReactNode;
}) {
  return (
    <PresentationEditorStoreContext.Provider value={store}>
      {children}
    </PresentationEditorStoreContext.Provider>
  );
}

export function usePresentationEditorStoreFromContext(): PresentationEditorStore {
  const store = useContext(PresentationEditorStoreContext);
  if (!store) {
    throw new Error('PresentationEditorStoreProvider is required for usePresentationEditorStoreFromContext');
  }
  return store;
}

export function usePresentationEditorState(): PresentationEditorState {
  const store = usePresentationEditorStoreFromContext();
  return useSyncExternalStore(store.subscribe, store.getState, store.getState);
}
